import { Helmet } from 'react-helmet-async';

const SITE      = 'MDS Transportation LLC';
const SITE_URL  = 'https://www.mdstransportation.com';
const OG_IMAGE  = `${SITE_URL}/images/fleet-lineup-wide.jpg`;
const PHONE     = '+1-317-982-1959';

const DEFAULT_DESC =
  'MDS Transportation LLC provides safe, reliable non-emergency medical transportation (NEMT) in Indianapolis, IN and Fort Wayne, IN. ' +
  'Wheelchair-accessible vehicles, senior transport, hospital discharge rides, and recurring medical appointment transportation.';

const DEFAULT_KW =
  'non-emergency medical transportation Indianapolis, NEMT Indianapolis, wheelchair accessible transportation, ' +
  'senior transportation Indiana, hospital discharge transport, medical ride service, Fort Wayne medical transportation, ' +
  'dialysis transport Indianapolis, therapy appointment transportation, NEMT provider Indiana';

/**
 * Drop <SEO /> at the top of any page component to set its meta tags,
 * Open Graph, Twitter Card, canonical URL, and JSON-LD structured data.
 *
 * Props:
 *  title       – page-level title (will be appended with "| MDS Transportation LLC")
 *  description – meta description (falls back to DEFAULT_DESC)
 *  path        – URL path e.g. "/services"
 *  keywords    – comma-separated keywords (falls back to DEFAULT_KW)
 *  image       – OG image URL
 *  schema      – JSON-LD object (use helpers below)
 *  noIndex     – set true for login/register/dashboard pages
 */
export default function SEO({
  title,
  description,
  path = '',
  keywords,
  image = OG_IMAGE,
  schema = null,
  noIndex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE}` : `${SITE} — Reliable Non-Emergency Medical Transportation`;
  const canonical = `${SITE_URL}${path}`;
  const desc      = description || DEFAULT_DESC;
  const kw        = keywords    || DEFAULT_KW;

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description"    content={desc} />
      <meta name="keywords"       content={kw} />
      <meta name="author"         content={SITE} />
      <meta name="robots"         content={noIndex ? 'noindex,nofollow' : 'index,follow,max-snippet:-1,max-image-preview:large'} />
      <link rel="canonical"       href={canonical} />

      {/* Open Graph */}
      <meta property="og:type"        content="website" />
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url"         content={canonical} />
      <meta property="og:site_name"   content={SITE} />
      <meta property="og:image"       content={image} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt"   content={fullTitle} />
      <meta property="og:locale"      content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content="@MDSTransportLLC" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image"       content={image} />

      {/* Geo */}
      <meta name="geo.region"    content="US-IN" />
      <meta name="geo.placename" content="Indianapolis, Indiana" />
      <meta name="geo.position"  content="39.7684;-86.1581" />
      <meta name="ICBM"          content="39.7684, -86.1581" />

      {/* JSON-LD */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

// ─── JSON-LD Schema Helpers ───────────────────────────────────────────────────

const businessRef = { '@id': `${SITE_URL}/#business` };

export const schemas = {

  // Home page — WebPage + breadcrumb
  home: {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#homepage`,
    url: SITE_URL,
    name: `${SITE} — Reliable Non-Emergency Medical Transportation`,
    description: DEFAULT_DESC,
    about: businessRef,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL }],
    },
  },

  // About page
  about: {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    url: `${SITE_URL}/about`,
    name: `About Us | ${SITE}`,
    description: 'Learn about MDS Transportation LLC — our mission, values, and commitment to compassionate medical transportation.',
    about: businessRef,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',     item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'About Us', item: `${SITE_URL}/about` },
      ],
    },
  },

  // Services page — ItemList of all 6 services
  services: {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'MDS Transportation Services',
    url: `${SITE_URL}/services`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',     item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${SITE_URL}/services` },
      ],
    },
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@type': 'Service', name: 'Non-Emergency Medical Transportation (NEMT)', description: 'Door-to-door rides to doctor appointments, dialysis, therapy, and specialist visits.', provider: businessRef, areaServed: 'Indianapolis, IN' } },
      { '@type': 'ListItem', position: 2, item: { '@type': 'Service', name: 'Wheelchair-Accessible Transportation',        description: 'ADA-compliant vehicles with ramps and lifts for wheelchair and mobility aid users.',             provider: businessRef, areaServed: 'Indianapolis, IN' } },
      { '@type': 'ListItem', position: 3, item: { '@type': 'Service', name: 'Senior & Assisted Transportation',            description: 'Specialized rides for seniors requiring extra care and door-through-door assistance.',           provider: businessRef, areaServed: 'Indianapolis, IN' } },
      { '@type': 'ListItem', position: 4, item: { '@type': 'Service', name: 'Hospital Discharge Transportation',           description: 'Safe, comfortable rides home directly from hospital discharge.',                               provider: businessRef, areaServed: 'Indianapolis, IN' } },
      { '@type': 'ListItem', position: 5, item: { '@type': 'Service', name: 'Special Needs Transportation',                description: 'Patient-centred transport for individuals with disabilities or chronic illness.',                 provider: businessRef, areaServed: 'Indianapolis, IN' } },
      { '@type': 'ListItem', position: 6, item: { '@type': 'Service', name: 'Recurring Transportation',                   description: 'Scheduled weekly or monthly plans for ongoing treatments like dialysis or chemo.',               provider: businessRef, areaServed: 'Indianapolis, IN' } },
    ],
  },

  // Contact page
  contact: {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    url: `${SITE_URL}/contact`,
    name: `Contact Us | ${SITE}`,
    description: 'Get in touch with MDS Transportation LLC for bookings, questions and support. Call +1 (317) 982-1959.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',    item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
      ],
    },
  },

  // Book a Ride — ReserveAction
  book: {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url: `${SITE_URL}/book`,
    name: `Book a Ride | ${SITE}`,
    description: 'Schedule your non-emergency medical transportation online. Easy 4-step booking form — available 24/7.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home',        item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Book a Ride', item: `${SITE_URL}/book` },
      ],
    },
    potentialAction: {
      '@type': 'ReserveAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/book` },
      result:  { '@type': 'Reservation', name: 'Ride Booking' },
    },
  },

  // FAQ — shown on Home and Services pages
  faq: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Non-Emergency Medical Transportation (NEMT)?',
        acceptedAnswer: { '@type': 'Answer', text: 'Non-Emergency Medical Transportation (NEMT) provides scheduled, safe rides to medical appointments for individuals who cannot drive themselves — including seniors, patients recovering from surgery, and people with mobility challenges. Unlike ambulances, NEMT is for planned, non-urgent travel.' },
      },
      {
        '@type': 'Question',
        name: 'Do you offer wheelchair-accessible vehicles?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. All our wheelchair-accessible vehicles are ADA-compliant, equipped with ramps, lifts, and secure tie-down systems. Our trained drivers assist with boarding and disembarking.' },
      },
      {
        '@type': 'Question',
        name: 'What areas do you serve?',
        acceptedAnswer: { '@type': 'Answer', text: 'MDS Transportation serves Indianapolis, Fort Wayne, Lawrence, Beech Grove, Speedway, and surrounding communities in Indiana and Maryland.' },
      },
      {
        '@type': 'Question',
        name: 'How do I book a ride?',
        acceptedAnswer: { '@type': 'Answer', text: `Book online at ${SITE_URL}/book, or call us at ${PHONE}. We are available 24/7.` },
      },
      {
        '@type': 'Question',
        name: 'Do you provide recurring transportation?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. We offer weekly, bi-weekly, or monthly recurring transport plans for patients with ongoing treatments such as dialysis, chemotherapy, or physical therapy.' },
      },
      {
        '@type': 'Question',
        name: 'Are your drivers trained and insured?',
        acceptedAnswer: { '@type': 'Answer', text: 'All MDS Transportation drivers are fully licensed, background-checked, insured, and trained in patient care, mobility assistance, and first aid.' },
      },
    ],
  },

  // Reviews — pass in testimonials array from API
  reviews: (testimonials = []) => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${SITE_URL}/#business`,
    name: SITE,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5.0',
      reviewCount: String(testimonials.length || 5),
      bestRating: '5',
      worstRating: '1',
    },
    review: testimonials.slice(0, 5).map((t) => ({
      '@type': 'Review',
      author:       { '@type': 'Person', name: t.author_name },
      reviewRating: { '@type': 'Rating', ratingValue: String(t.rating || 5) },
      reviewBody:   t.content,
      datePublished: t.created_at
        ? new Date(t.created_at).toISOString().split('T')[0]
        : '2024-01-01',
    })),
  }),
};
